"use client";

/**
 * Pre-step (talent-search entry only) — "Add to a position".
 *
 * Talent-search profiles may or may not already be on a job, so before enrolling
 * we pick the position and reconcile membership in one place:
 *
 *   - On job pick we call `resolve-candidates` to learn which of the selected
 *     profiles are already in that job (with their `job_candidate_id`s) and which
 *     still need adding. The copy reflects this ("X of Y aren't on this position
 *     yet", or "N profiles" when none are).
 *   - On Continue we add only the missing profiles (at the chosen stage), then
 *     hand the FULL cohort forward — already-present candidates included — so the
 *     wizard enrolls everyone the user picked.
 *   - If everyone is already on the job, there's nothing to add: we skip the add
 *     call and continue straight into enrollment. The user is never bounced
 *     elsewhere to "finish" — enrollment always completes from here.
 */

import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button, SubmitButton } from "@/components/ui/button";
import { showToast } from "@/components/ui/sonner";
import { StepFooter } from "@/components/wizard";
import { JobInput, StageSelect } from "@/features/jobs";
import { bustPipelineCache } from "@/features/jobs/lib/bust-pipeline-cache";
import { useSelectedProject } from "@/hooks/use-search";
import { addFromSearch, resolveCandidates } from "@/lib/api/generated";
import type { Job } from "@/lib/api/types";
import {
  getDefaultAddToJobStageId,
  type AddToJobJobOption,
  type AddToJobStageOption,
} from "@/lib/search/add-to-job";

import { ENROLLMENT_PRIMARY_BTN } from "../../lib/theme";

export interface StepAddToPositionResult {
  jobId: string;
  jobCandidateIds: string[];
}

export interface StepAddToPositionProps {
  /** Talent-search profile UUIDs to enroll (some may already be on a job). */
  profileIds: string[];
  /** Fires with the full cohort once everyone is on the job — added + present. */
  onAdded: (result: StepAddToPositionResult) => void;
  /** Close the whole modal (Cancel / X). */
  onCancel: () => void;
}

function normalizeJob(job: Job): AddToJobJobOption {
  return {
    id: job.id,
    title: job.title,
    stages: (job.stages ?? []).map(
      (stage): AddToJobStageOption => ({
        id: stage.id,
        name: stage.name,
        position: stage.position,
        is_system: stage.is_system,
      }),
    ),
  };
}

export function StepAddToPosition({
  profileIds,
  onAdded,
  onCancel,
}: StepAddToPositionProps) {
  const queryClient = useQueryClient();
  const { data: selectedProject } = useSelectedProject();

  const [selectedJob, setSelectedJob] =
    React.useState<AddToJobJobOption | null>(null);
  const [selectedStageId, setSelectedStageId] = React.useState<string | null>(
    null,
  );

  const activeJobId = selectedJob?.id ?? null;
  const total = profileIds.length;
  const profileWord = total === 1 ? "profile" : "profiles";

  // Stable key for the membership lookup (selection order is irrelevant).
  const profileKey = React.useMemo(
    () => [...profileIds].sort().join(","),
    [profileIds],
  );

  // Read-only check: which of the picked profiles are already on this job?
  const membershipQuery = useQuery({
    queryKey: ["enroll-resolve-candidates", activeJobId, profileKey],
    queryFn: () =>
      resolveCandidates(activeJobId as string, { profile_ids: profileIds }),
    enabled: !!activeJobId && total > 0,
  });

  const membership = membershipQuery.data;
  const membershipLoading = !!activeJobId && membershipQuery.isLoading;
  const membershipError = !!activeJobId && membershipQuery.isError;
  // If the check fails we fall back to "add everyone" so the user is never
  // blocked — that path also needs a stage.
  const presentJobCandidateIds = membership?.present_job_candidate_ids ?? [];
  const presentCount = membership?.present_profile_ids?.length ?? 0;
  const absentProfileIds = membership?.absent_profile_ids ?? profileIds;
  const needAddCount = membership ? absentProfileIds.length : total;
  const allPresent = !!membership && needAddCount === 0;

  const addMutation = useMutation({
    mutationFn: async (): Promise<StepAddToPositionResult> => {
      if (!activeJobId) throw new Error("Select a position first.");

      // Everyone is already on the job — skip the add and enroll them as-is.
      if (allPresent) {
        return { jobId: activeJobId, jobCandidateIds: presentJobCandidateIds };
      }

      const result = await addFromSearch(activeJobId, {
        profile_ids: absentProfileIds,
        stage_id: selectedStageId ?? undefined,
      });
      const addedIds = result.job_candidate_ids ?? [];

      // Plain invalidate is unreliable under keepPreviousData — match add-to-job.
      bustPipelineCache(queryClient, activeJobId, { includeJob: true });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });

      if (result.added && presentCount > 0) {
        showToast.info("Added to position", {
          description: `${result.added} added · ${presentCount} already on the job.`,
        });
      } else if (result.added) {
        showToast.success("Added to position", {
          description: `${result.added} ${result.added === 1 ? "profile" : "profiles"} added — continue to enroll.`,
        });
      }

      // The cohort is everyone the user picked: freshly added + already present.
      return {
        jobId: activeJobId,
        jobCandidateIds: [...presentJobCandidateIds, ...addedIds],
      };
    },
    onSuccess: (result) => {
      if (result.jobCandidateIds.length === 0) {
        // Nothing resolvable (e.g. profiles no longer exist) — stay put instead
        // of advancing into an empty enrollment.
        showToast.error("Nothing to enroll", {
          description: "These profiles couldn't be added. Please try again.",
        });
        return;
      }
      onAdded(result);
    },
    onError: (error) => {
      showToast.error("Could not add to position", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    },
  });

  const handleSelectJob = React.useCallback((job: Job) => {
    const normalized = normalizeJob(job);
    setSelectedJob(normalized);
    setSelectedStageId(getDefaultAddToJobStageId(normalized));
  }, []);

  // The stage only appears AFTER we know some profiles need adding — never while
  // the check is still running, so it can't flash in and then disappear when
  // everyone turns out to already be on the job. (On a failed check we show it
  // because the fallback adds everyone.)
  const showStage = membershipError || (!!membership && needAddCount > 0);
  const submitDisabled =
    addMutation.isPending ||
    !activeJobId ||
    membershipLoading ||
    (showStage && !selectedStageId) ||
    total === 0;

  // Status sits BELOW the position picker so the step reads top-to-bottom:
  // pick a position → see what it means → (if needed) pick a stage. Nothing is
  // shown until a position is chosen.
  let status: React.ReactNode = null;
  if (activeJobId) {
    if (membershipLoading) {
      status = "Checking who's already on this position…";
    } else if (membershipError) {
      status = `We couldn't check this position. Pick a stage below; we'll add anyone who isn't on it, then enroll all ${total}.`;
    } else if (allPresent) {
      status = `All ${total} ${profileWord} are already on this position. Continue to enroll them.`;
    } else if (presentCount > 0) {
      status = `${needAddCount} of ${total} profiles aren't on this position yet. Pick a stage below to add them. The rest are already here.`;
    } else {
      status = `${total} ${profileWord} will be added to this position. Pick a stage below.`;
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col px-6 pb-5 pt-6 sm:px-8">
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        {!selectedProject ? (
          <div className="rounded-lg border border-border bg-surface-header p-4 text-sm text-muted-foreground">
            Select a project before adding profiles to a position.
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <p className="pr-8 text-sm font-medium leading-5 text-heading">
                Position
              </p>
              <JobInput
                value={
                  selectedJob
                    ? { id: selectedJob.id, title: selectedJob.title }
                    : null
                }
                onChange={handleSelectJob}
              />
              {status && (
                <p className="text-sm leading-normal text-muted-foreground">
                  {status}
                </p>
              )}
            </div>

            {showStage && (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium leading-5 text-heading">
                  Stage
                </p>
                <StageSelect
                  jobId={activeJobId}
                  value={selectedStageId}
                  onChange={setSelectedStageId}
                  disabled={!activeJobId}
                  showRecommendedBadge
                />
              </div>
            )}
          </>
        )}
      </div>

      <StepFooter>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <SubmitButton
          type="button"
          size="sm"
          onClick={() => addMutation.mutate()}
          disabled={submitDisabled}
          loading={addMutation.isPending}
          loadingLabel="Adding"
          className={ENROLLMENT_PRIMARY_BTN}
        >
          Continue
        </SubmitButton>
      </StepFooter>
    </div>
  );
}

export default StepAddToPosition;
